(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.ReutsCirclePopup = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => api.init(document), { once: true });
    else api.init(document);
    document.addEventListener('shopify:section:load', event => api.init(event.target));
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const DAY = 24 * 60 * 60 * 1000;

  // Shared policy is exercised by regression tests; DOM handling lives below.
  function decide(s) {
    if (!Number.isFinite(s.expiresAt) || s.now >= s.expiresAt) return 'expired';
    if (s.excluded) return 'excluded';
    if (s.dismissed) return 'dismissed';
    if (!s.preview && s.clicked) return 'clicked';
    if (!s.preview && !s.resuming && s.lastShownAt > 0 && s.now - s.lastShownAt < DAY) return 'frequency';
    if (s.blocked || !s.visible) return s.open ? 'yield' : 'blocked';
    if (!s.inventoryReady) return 'inventory-pending';
    if (!s.available) return 'sold-out';
    if (s.open) return 'open';
    if (s.now < s.notBefore) return 'delay';
    if (!s.preview && !s.activeRecently) return 'idle';
    return 'show';
  }

  function excludedPath(path) {
    return /\/(?:cart|checkout|checkouts|account)(?:\/|$)/.test(path) || /\/products\/reuts-circle(?:-|\/|$)/.test(path) || /\/pages\/reuts-circle(?:-|\/|$)/.test(path);
  }

  function init(scope) {
    if (window.Shopify && window.Shopify.designMode) return;
    scope.querySelectorAll('.rh-circle-popup-root').forEach(root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const dialog = root.querySelector('dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const preview = new URLSearchParams(location.search).get('rh_circle_preview') === '1';
      const key = 'rh-circle-popup:' + root.dataset.campaign;
      const expiresAt = Date.parse(root.dataset.expiresAt);
      const read = suffix => { try { return localStorage.getItem(key + suffix); } catch (_) { return null; } };
      const write = (suffix, value) => { try { localStorage.setItem(key + suffix, String(value)); } catch (_) { /* In-memory dismissal still works when browser storage is disabled. */ } };
      const initialDelay = preview ? 2000 : (Number(root.dataset.initialDelay) || 10) * 1000;
      const quietDelay = preview ? 2000 : (Number(root.dataset.quietDelay) || 25) * 1000;
      let notBefore = Date.now() + initialDelay;
      let lastActivity = Date.now();
      let wasBlocked = false;
      let dismissed = false;
      let resuming = false;
      let stopped = false;
      let available = false;
      let inventoryReady = false;
      let checkedAt = 0;
      let inventoryRequest = null;
      let previousFocus = null;
      let closeReason = '';
      const observedRoots = new WeakSet();
      const cleanups = [];
      const status = value => { if (root.dataset.circlePopupStatus !== value) root.dataset.circlePopupStatus = value; };
      const listen = (target, type, callback, options) => {
        target.addEventListener(type, callback, options);
        cleanups.push(() => target.removeEventListener(type, callback, options));
      };
      function visible(el) {
        const style = getComputedStyle(el);
        return !el.closest('[hidden], [aria-hidden="true"]') && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && el.getClientRects().length > 0;
      }
      function blockers() {
        for (const host of document.querySelectorAll('flashy-popup')) {
          const shadow = host.shadowRoot;
          if (shadow && !observedRoots.has(shadow)) {
            observer.observe(shadow, observation);
            observedRoots.add(shadow);
          }
          if (shadow && [...shadow.querySelectorAll('dialog[open], .fls-popup-overlay, [role="dialog"]')].some(visible)) return true;
          if (!shadow && host.getClientRects().length && visible(host)) return true;
        }
        const selectors = 'dialog[open], [role="dialog"][aria-modal="true"], #shopify-pc__banner, #shopify-pc__prefs, cart-drawer.active, #search-modal.active, .popup.popup-open, modal-dialog[open], .contact-launcher.is-open, details.menu-opening';
        if ([...document.querySelectorAll(selectors)].some(el => !root.contains(el) && visible(el))) return true;
        return [...document.body.classList].some(name => name === 'overflow-hidden' || name.startsWith('overflow-hidden-'));
      }
      function close(reason) {
        closeReason = reason;
        if (dialog.open) dialog.close();
        document.documentElement.classList.remove('rh-circle-popup-open');
      }
      function stop(reason) {
        if (stopped) return;
        stopped = true;
        close(reason);
        clearInterval(timer);
        observer.disconnect();
        cleanups.forEach(cleanup => cleanup());
        status(reason);
      }
      function dismiss() {
        dismissed = true;
        stop('dismissed');
        if (previousFocus && previousFocus.isConnected && !blockers()) previousFocus.focus({ preventScroll: true });
      }
      async function refreshInventory() {
        if (inventoryRequest || stopped) return inventoryRequest;
        inventoryRequest = (async () => {
          const abort = new AbortController();
          const timeout = setTimeout(() => abort.abort(), 6000);
          try {
            const url = new URL(root.dataset.productUrl, location.origin);
            url.pathname = url.pathname.replace(/\/$/, '') + '.js';
            const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: abort.signal, headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error('Product lookup failed');
            const product = await response.json();
            available = product.available === true && product.variants.some(variant => variant.available === true);
            inventoryReady = true;
          } catch (_) {
            inventoryReady = false;
          } finally {
            checkedAt = Date.now();
            clearTimeout(timeout);
            inventoryRequest = null;
          }
          if (!stopped) tick();
        })();
        return inventoryRequest;
      }
      function tick() {
        if (stopped) return;
        if (!root.isConnected) { stop('removed'); return; }
        const now = Date.now();
        const blocked = blockers() || document.visibilityState !== 'visible';
        if (wasBlocked && !blocked) notBefore = now + quietDelay;
        wasBlocked = blocked;
        const action = decide({ now, expiresAt, excluded: excludedPath(location.pathname), dismissed,
          preview, clicked: read(':clicked') === 'true', lastShownAt: Number(read(':shown-at')) || 0,
          resuming, blocked, visible: document.visibilityState === 'visible', open: dialog.open,
          inventoryReady, available, notBefore, activeRecently: now - lastActivity < 60000 });
        if (['expired', 'excluded', 'dismissed', 'clicked', 'frequency', 'sold-out'].includes(action)) { stop(action); return; }
        if (action === 'yield') {
          resuming = true;
          close('yield');
          status('waiting-for-other-window');
        } else if (action === 'show') {
          if (now - checkedAt > 30000) { inventoryReady = false; refreshInventory(); status('inventory-pending'); return; }
          previousFocus = document.activeElement;
          dialog.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
          dialog.showModal();
          document.documentElement.classList.add('rh-circle-popup-open');
          resuming = true;
          if (!preview) write(':shown-at', now);
          status('showing');
        } else if (action !== 'open') status(action);
        if (now - checkedAt > (inventoryReady ? 30000 : 15000)) refreshInventory();
      }
      const observation = { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'style', 'hidden', 'aria-hidden'] };
      const observer = new MutationObserver(tick);
      observer.observe(document.documentElement, observation);
      const timer = setInterval(tick, 250);
      ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach(type => listen(document, type, () => { lastActivity = Date.now(); }, { passive: true }));
      listen(document, 'visibilitychange', tick);
      listen(window, 'storage', tick);
      listen(dialog.querySelector('[data-circle-popup-close]'), 'click', dismiss);
      listen(dialog, 'cancel', event => { event.preventDefault(); dismiss(); });
      listen(dialog, 'close', () => { document.documentElement.classList.remove('rh-circle-popup-open'); if (!closeReason) dismiss(); closeReason = ''; });
      listen(dialog, 'click', event => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dismiss();
      });
      listen(dialog.querySelector('[data-circle-popup-cta]'), 'click', () => { if (!preview) write(':clicked', true); stop('clicked'); });
      tick();
      if (!stopped) refreshInventory();
    });
  }
  return { DAY, decide, excludedPath, init };
});
